import { useEffect, useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { BriefSubscribe } from "@/components/BriefSubscribe";
import { CrisisNotice } from "@/components/CrisisNotice";
import { SpecialtyAudiencePanel } from "@/components/SpecialtyAudiencePanel";
import { PublicShell } from "@/components/layout/PublicShell";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { isPsychiatrySpecialty } from "@/lib/crisis-notice";
import {
  usePublicDirectory,
  usePublicSpecialties,
} from "@/lib/public-directory";
import { specialtyPageNoindex } from "@/lib/copy-review";
import { SEO, specialtySeoMetadata } from "@/lib/seo";
import { buildAreas } from "@/lib/local-pages";
import { breadcrumbStructuredData, relatedSpecialties, specialtyAreaPath } from "@/lib/local-seo";
import {
  buildSpecialtyCollectionStructuredData,
  isThinSpecialty,
  specialtyHeading,
} from "@/lib/structured-data";
import { SearchX } from "lucide-react";
import { ListingCard, ListingSkeletons } from "@/components/ListingCard";
import { specialtyContent } from "@/lib/specialty-content";
import { Button, ButtonLink, EmptyState } from "@/components/system";

export default function SpecialtyPage() {
  const { specialtySlug = "" } = useParams<{ specialtySlug: string }>();
  const { city } = useCity();
  const trackedSlug = useRef<string | null>(null);
  const specialties = usePublicSpecialties(city.slug);
  const directory = usePublicDirectory(city.slug, {
    type: "specialist",
    specialty: specialtySlug,
    limit: 100,
  });
  const specialty = specialties.data?.find((item) => item.slug === specialtySlug);

  useEffect(() => {
    if (!specialty || trackedSlug.current === specialty.slug) return;
    trackedSlug.current = specialty.slug;
    track("specialty_page_view", { specialty_slug: specialty.slug });
  }, [specialty]);

  if (specialtySlug.toLowerCase() === "specialist") {
    return <Navigate to="/directory" replace />;
  }

  const fallbackName = specialtyNameFromSlug(specialtySlug);
  const metadata = specialtySeoMetadata({
    slug: specialtySlug,
    name: specialty?.name ?? fallbackName,
    count: specialty?.count ?? 0,
    cityName: city.name,
  });
  const specialists = directory.data?.items ?? [];
  // Suburbs with enough of this specialty for their own page (≥3 listings).
  const areaLinks = buildAreas(
    specialists.map((item) => ({
      slug: item.slug,
      name: item.name,
      specialty: item.address_json.specialty,
      suburb: item.address_json.suburb,
    })),
  ).filter((area) => !isThinSpecialty(area.count));
  const related = relatedSpecialties(specialtySlug, specialties.data ?? []);

  return (
    <>
      {/* Head tags wait for the specialty to load, so the loading state never
          replaces the prerendered head with "0 specialists" (report 16). */}
      {specialty ? (
      <SEO
        noindex={specialtyPageNoindex(specialty.slug, specialty.count)}
        follow
        title={metadata.title}
        description={metadata.description}
        path={metadata.path}
        jsonLd={[
          buildSpecialtyCollectionStructuredData({
            slug: specialtySlug,
            name: specialty?.name ?? fallbackName,
            count: specialty?.count ?? specialists.length,
            cityName: city.name,
            specialists: specialists.map((item) => ({
              slug: item.slug,
              name: item.name,
              suburb: item.address_json.suburb,
            })),
          }),
          breadcrumbStructuredData([
            { name: "Home", path: "/" },
            { name: "Directory", path: "/directory" },
            { name: specialtyHeading(specialtySlug, specialty.name, city.name), path: metadata.path },
          ]),
        ]}
      />
      ) : null}
      <PublicShell className="pb-specialty-page">
        <div className="ds-container">
          <header className="ds-page-head">
            <ol className="ds-crumbs" aria-label="Breadcrumb">
              <li><Link to="/">Home</Link></li>
              <li><Link to="/directory">Directory</Link></li>
            </ol>
            <p className="ds-eyebrow ds-eyebrow--teal">{city.name} specialist directory</p>
            <h1 className="ds-display-1">{specialtyHeading(specialtySlug, specialty?.name ?? fallbackName, city.name)}</h1>
            {specialty ? (
              <div className="ds-facts">
                <span><b className="ds-num">{specialty.count}</b> listed</span>
                {/* "0 accepting referrals" read as "nobody is taking patients" (report 14). */}
                <span>
                  {specialty.acceptingCount > 0 ? (
                    <><b className="ds-num">{specialty.acceptingCount}</b> accepting referrals</>
                  ) : (
                    "Availability not yet published"
                  )}
                </span>
              </div>
            ) : null}
          </header>

          <div className="ds-stack" style={{ gap: 16 }}>
            {isPsychiatrySpecialty(specialtySlug) ? <CrisisNotice /> : null}
            <SpecialtyAudiencePanel slug={specialtySlug} name={specialty?.name ?? fallbackName} />
          </div>

          <section className="ds-section" aria-labelledby="specialty-listings-heading">
            <div className="ds-section-head">
              <div>
                <h2 id="specialty-listings-heading" className="ds-display-2">
                  All {specialtyContent(specialtySlug)?.practitionerPlural ?? `${(specialty?.name ?? fallbackName).toLowerCase()} specialists`}
                </h2>
              </div>
              <ButtonLink to="/directory" variant="secondary">All specialties</ButtonLink>
            </div>

            {specialties.isLoading || directory.isLoading ? (
              <ListingSkeletons />
            ) : specialties.isError || directory.isError ? (
              <EmptyState
                tone="error"
                icon={<SearchX />}
                title="This page didn't load"
                action={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      void specialties.refetch();
                      void directory.refetch();
                    }}
                  >
                    Try again
                  </Button>
                }
              >
                We could not load the current listings. Check your connection and try again.
              </EmptyState>
            ) : !specialty ? (
              <EmptyState icon={<SearchX />} title="Specialty not found" action={<ButtonLink to="/directory">Browse the directory</ButtonLink>}>
                This specialty may have moved or is not listed in {city.name} yet.
              </EmptyState>
            ) : specialists.length === 0 ? (
              <EmptyState icon={<SearchX />} title="No listings to show yet">
                Listings appear here once their details have been checked against a public source.
              </EmptyState>
            ) : (
              <div className="ds-grid">
                {specialists.map((facility) => <ListingCard key={facility.id} facility={facility} />)}
              </div>
            )}
          </section>

          {areaLinks.length > 0 || related.length > 0 ? (
            <section className="ds-section" aria-label="More ways to browse">
              <div className="ds-grid ds-grid--2">
                {areaLinks.length > 0 ? (
                  <nav className="ds-stack" style={{ gap: 12 }} aria-labelledby="specialty-areas">
                    <h2 id="specialty-areas" className="ds-title">By area</h2>
                    <ul className="ds-link-grid">
                      {areaLinks.map((area) => (
                        <li key={area.slug}>
                          <Link to={specialtyAreaPath(specialtySlug, area.slug)}>
                            <span>{specialtyHeading(specialtySlug, specialty?.name ?? fallbackName, area.name)}</span>
                            <span className="ds-num">{area.count}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>
                ) : null}
                {related.length > 0 ? (
                  <nav className="ds-stack" style={{ gap: 12 }} aria-labelledby="specialty-related">
                    <h2 id="specialty-related" className="ds-title">Related specialties</h2>
                    <ul className="ds-link-grid">
                      {related.map((item) => (
                        <li key={item.slug}>
                          <Link to={`/directory/${item.slug}`}>
                            <span>{specialtyHeading(item.slug, item.name, city.name)}</span>
                            <span className="ds-num">{item.count}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>
                ) : null}
              </div>
            </section>
          ) : null}

          <div style={{ marginTop: 16 }}>
            <BriefSubscribe sourcePath={`/directory/${specialtySlug}`} />
          </div>
        </div>
      </PublicShell>
    </>
  );
}

function specialtyNameFromSlug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
